import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AuthGuard } from '@/components/AuthGuard'
import { ErrorBoundary, HubErrorFallback, ProjectErrorFallback } from '@/components/ErrorBoundary'

// Layouts
import { PublicLayout } from '@/layouts/PublicLayout'
import { HubLayout } from '@/layouts/HubLayout'
import { ProjectLayout } from '@/layouts/ProjectLayout'
import { AdminLayout } from '@/layouts/AdminLayout'

// Lazy-load pages to keep initial bundle small
import { lazy, Suspense } from 'react'

function lazyPage(importFn: () => Promise<{ default: React.ComponentType }>) {
  const Component = lazy(importFn)
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Component />
    </Suspense>
  )
}

function PageSkeleton() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="animate-pulse text-muted-foreground">Loading...</div>
    </div>
  )
}

// --- Public pages ---
const LandingPage = () => lazyPage(() => import('@/pages/Landing'))
const LoginPage = () => lazyPage(() => import('@/pages/Login'))
const RegisterPage = () => lazyPage(() => import('@/pages/Register'))
const FleetPage = () => lazyPage(() => import('@/pages/Fleet'))
const PricingPage = () => lazyPage(() => import('@/pages/Pricing'))
const ApplyPage = () => lazyPage(() => import('@/pages/Apply'))
const ResetPasswordPage = () => lazyPage(() => import('@/pages/ResetPassword'))
const VerifyEmailPage = () => lazyPage(() => import('@/pages/VerifyEmail'))

// --- Hub pages ---
const DashboardPage = () => lazyPage(() => import('@/pages/hub/Dashboard'))
const ProjectsPage = () => lazyPage(() => import('@/pages/hub/Projects'))
const BillingPage = () => lazyPage(() => import('@/pages/hub/Billing'))
const NotificationSettingsPage = () => lazyPage(() => import('@/pages/hub/NotificationSettings'))
const ProfilePage = () => lazyPage(() => import('@/pages/hub/Profile'))
const CaptainFleetPage = () => lazyPage(() => import('@/pages/hub/CaptainFleet'))

// --- Admiral pages ---
const AdmiralSquadronsPage = () => lazyPage(() => import('@/pages/hub/admiral/Squadrons'))
const AdmiralSquadronDetailPage = () => lazyPage(() => import('@/pages/hub/admiral/SquadronDetail'))

// --- Admin pages ---
const AdminFleetPage = () => lazyPage(() => import('@/pages/hub/admin/Fleet'))
const AdminSquadronsPage = () => lazyPage(() => import('@/pages/hub/admin/Squadrons'))
const AdminFinancesPage = () => lazyPage(() => import('@/pages/hub/admin/Finances'))
const AdminUsersPage = () => lazyPage(() => import('@/pages/hub/admin/Users'))
const AdminInvitesPage = () => lazyPage(() => import('@/pages/hub/admin/Invites'))

// --- Project pages ---
const ProjectOverviewPage = () => lazyPage(() => import('@/pages/project/Overview'))
const SurveysPage = () => lazyPage(() => import('@/pages/project/Surveys'))
const SurveyDetailPage = () => lazyPage(() => import('@/pages/project/SurveyDetail'))
const MilestonesPage = () => lazyPage(() => import('@/pages/project/Milestones'))
const DiscussionsPage = () => lazyPage(() => import('@/pages/project/Discussions'))
const DiscussionDetailPage = () => lazyPage(() => import('@/pages/project/DiscussionDetail'))
const WikiPage = () => lazyPage(() => import('@/pages/project/Wiki'))
const KanbanPage = () => lazyPage(() => import('@/pages/project/Kanban'))
const StandupsPage = () => lazyPage(() => import('@/pages/project/Standups'))
const TimeTrackingPage = () => lazyPage(() => import('@/pages/project/TimeTracking'))
const ReviewsPage = () => lazyPage(() => import('@/pages/project/Reviews'))
const PollsPage = () => lazyPage(() => import('@/pages/project/Polls'))
const BookmarksPage = () => lazyPage(() => import('@/pages/project/Bookmarks'))
const RetrosPage = () => lazyPage(() => import('@/pages/project/Retros'))
const OkrsPage = () => lazyPage(() => import('@/pages/project/Okrs'))
const FilesPage = () => lazyPage(() => import('@/pages/project/Files'))
const CommentsPage = () => lazyPage(() => import('@/pages/project/Comments'))
const WhiteboardPage = () => lazyPage(() => import('@/pages/project/Whiteboard'))
const ContentCalendarPage = () => lazyPage(() => import('@/pages/project/ContentCalendar'))
const FinancesPage = () => lazyPage(() => import('@/pages/project/Finances'))
const CrewManifestPage = () => lazyPage(() => import('@/pages/project/CrewManifest'))
const CrewDeckPage = () => lazyPage(() => import('@/pages/project/CrewDeck'))
const OnboardingPage = () => lazyPage(() => import('@/pages/project/Onboarding'))
const SettingsPage = () => lazyPage(() => import('@/pages/project/Settings'))
const CaptainPage = () => lazyPage(() => import('@/pages/project/Captain'))
const CaptainOnboardingPage = () => lazyPage(() => import('@/pages/project/CaptainOnboarding'))
const CaptainRecruitmentPage = () => lazyPage(() => import('@/pages/project/CaptainRecruitment'))

export const router = createBrowserRouter([
  {
    errorElement: <ErrorBoundary />,
    children: [
      // --- Public routes (no auth required) ---
      {
        element: <PublicLayout />,
        children: [
          { path: '/', element: <LandingPage /> },
          { path: '/login', element: <LoginPage /> },
          { path: '/register', element: <RegisterPage /> },
          { path: '/fleet', element: <FleetPage /> },
          { path: '/pricing', element: <PricingPage /> },
          { path: '/apply/:positionId', element: <ApplyPage /> },
          { path: '/reset-password', element: <ResetPasswordPage /> },
          { path: '/verify-email', element: <VerifyEmailPage /> },
        ],
      },

      // --- Authenticated routes ---
      {
        element: <AuthGuard />,
        children: [
          // Hub (main dashboard shell)
          {
            path: '/hub',
            element: <HubLayout />,
            errorElement: <HubErrorFallback />,
            children: [
              { index: true, element: <DashboardPage /> },
              { path: 'projects', element: <ProjectsPage /> },
              { path: 'billing', element: <BillingPage /> },
              { path: 'notification-settings', element: <NotificationSettingsPage /> },
              { path: 'profiles/:userId', element: <ProfilePage /> },
              { path: 'captain/fleet', element: <CaptainFleetPage /> },

              // Admiral
              { path: 'admiral/squadrons', element: <AdmiralSquadronsPage /> },
              { path: 'admiral/squadrons/:id', element: <AdmiralSquadronDetailPage /> },

              // Admin
              {
                path: 'admin',
                element: <AdminLayout />,
                children: [
                  { path: 'fleet', element: <AdminFleetPage /> },
                  { path: 'squadrons', element: <AdminSquadronsPage /> },
                  { path: 'finances', element: <AdminFinancesPage /> },
                  { path: 'users', element: <AdminUsersPage /> },
                  { path: 'invites', element: <AdminInvitesPage /> },
                  { path: 'recruitment', element: <Navigate to="/hub/admin/fleet" replace /> },
                ],
              },

              // Project detail (nested under hub)
              {
                path: 'projects/:id',
                element: <ProjectLayout />,
                errorElement: <ProjectErrorFallback />,
                children: [
                  { index: true, element: <ProjectOverviewPage /> },
                  { path: 'surveys', element: <SurveysPage /> },
                  { path: 'surveys/:surveyId', element: <SurveyDetailPage /> },
                  { path: 'milestones', element: <MilestonesPage /> },
                  { path: 'discussions', element: <DiscussionsPage /> },
                  { path: 'discussions/:did', element: <DiscussionDetailPage /> },
                  { path: 'wiki', element: <WikiPage /> },
                  { path: 'kanban', element: <KanbanPage /> },
                  { path: 'standups', element: <StandupsPage /> },
                  { path: 'time-tracking', element: <TimeTrackingPage /> },
                  { path: 'reviews', element: <ReviewsPage /> },
                  { path: 'polls', element: <PollsPage /> },
                  { path: 'bookmarks', element: <BookmarksPage /> },
                  { path: 'retros', element: <RetrosPage /> },
                  { path: 'okrs', element: <OkrsPage /> },
                  { path: 'files', element: <FilesPage /> },
                  { path: 'comments', element: <CommentsPage /> },
                  { path: 'whiteboard', element: <WhiteboardPage /> },
                  { path: 'content-calendar', element: <ContentCalendarPage /> },
                  { path: 'finances', element: <FinancesPage /> },
                  { path: 'crew-manifest', element: <CrewManifestPage /> },
                  { path: 'crew-deck', element: <CrewDeckPage /> },
                  { path: 'onboarding', element: <OnboardingPage /> },
                  { path: 'settings', element: <SettingsPage /> },
                  { path: 'captain', element: <CaptainPage /> },
                  { path: 'captain/onboarding', element: <CaptainOnboardingPage /> },
                  { path: 'captain/recruitment', element: <CaptainRecruitmentPage /> },
                ],
              },
            ],
          },
        ],
      },

      // Catch-all → landing
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
